/**
 * ─────────────────────────────────────────────────────────────────────────
 * Neobrutalism Design Tokens (TypeScript Source of Truth)
 * Aligned 1:1 with src/styles/tokens.css & DESIGN.md
 * ─────────────────────────────────────────────────────────────────────────
 */

/**
 * Grosor de trazo de los íconos (lucide, viewBox de 24). Es relativo: escala
 * con el tamaño del ícono. Lo usa `<Icon>` por defecto y lo pasan a mano los
 * usos directos de lucide (`strokeWidth={ICON_STROKE}`), así el peso de todos
 * los íconos de la app se ajusta desde acá. Bajó de 2.5 a 2.2: con los íconos
 * del home y el dock a 68px, 2.5 quedaba pesado al lado del texto.
 */
export const ICON_STROKE = 2.2;

/**
 * Escala cerrada de tamaños de ícono, en px (lucide, viewBox de 24). Todo `size`
 * de un ícono de pantalla sale de acá (`<Icon size={ICON_SIZE.md} />`), no de un
 * número suelto: sm = chips y badges, md = botones, lg = el default, xl = acción
 * grande, hero = estado vacío o ícono protagonista. Los íconos que escalan con
 * su caja (`size="100%"`, `"40%"`) no entran: ahí manda el contenedor.
 */
export const ICON_SIZE = { sm: 16, md: 20, lg: 24, xl: 32, hero: 48 } as const;

export const NEO_COLORS = {
  // Primitives
  black: '#000000',
  ink: '#121212',
  white: '#FFFFFF',
  bg: '#FAF7F2',
  surfaceSubtle: '#F4EFEA',

  // Saturated Neobrutalist Accents
  yellow: '#FFE600',
  yellowDark: '#F59E0B',
  pink: '#FF66C4',
  pinkDark: '#E11D48',
  cyan: '#00F0FF',
  cyanDark: '#0284C7',
  lime: '#A3E635',
  limeDark: '#65A30D',
  green: '#39FF14',
  orange: '#FF914D',
  orangeDark: '#EA580C',
  purple: '#C78BFF',
  purpleDark: '#7E22CE',
  blue: '#70D6FF',
  red: '#FF4D4D',
  redDark: '#DC2626',
} as const;

/**
 * Las dos tintas de cada color de la ropa de Vestir (`--ropa-<color>` y `--ropa-<color>-2` de
 * tokens.css), en minúsculas como las deja `lib/mascotSvg.ts`. Los SVG de cada prenda se dibujan con
 * las tintas de su color de fábrica (el de `PRENDAS`) y Vestir las repinta al color que se elija:
 * son las claves de ese repintado. En el editor de piezas son los colores "de la prenda".
 */
export const ROPA = {
  rosa: { a: '#f28bb5', b: '#ffe066' },
  lila: { a: '#b79be8', b: '#6b4bb8' },
  celeste: { a: '#7cc8e8', b: '#2b7fa8' },
  amarillo: { a: '#f7d154', b: '#e2584a' },
  rojo: { a: '#e2584a', b: '#a82d22' },
  verde: { a: '#7fc98a', b: '#3a8a4a' },
} as const;

/** El trazo y el blanco de los dibujos de la figura (`--mascot-ink` y `--mascot-eye-white`). */
export const MASCOT_INK = '#120e0e';
export const MASCOT_WHITE = '#ffffff';

export const NEO_TINTS = {
  pinkLight: '#FFF0F7',
  roseLight: '#FFF2F6',
  coralLight: '#FFF1F0',
  orangeLight: '#FFF5EC',
  yellowLight: '#FFFDE6',
  limeLight: '#F5FCE8',
  greenLight: '#EBFCE9',
  aquaLight: '#E8FDFF',
  tealLight: '#EDF7FF',
  blueLight: '#EBF3FF',
  indigoLight: '#F2EEFF',
  purpleLight: '#F8EFFF',
  violetLight: '#FAEFFF',
  darkLight: '#EFEFEF',
} as const;

export const PIZARRA_PALETTE = [
  '#FF4D4D', // Rojo
  '#FF66C4', // Rosa
  '#FFE600', // Amarillo
  '#A3E635', // Verde Lima
  '#00F0FF', // Cyan
  '#C78BFF', // Violeta
  '#000000', // Tinta negra
] as const;

export const CONFETTI_COLORS = [
  '#FFE600',
  '#FF66C4',
  '#00F0FF',
  '#A3E635',
  '#FF914D',
  '#C78BFF',
  '#70D6FF',
  '#39FF14',
  '#FF4D4D',
] as const;

export const LIBRO_TOKENS = {
  forest: '#2D4A3E',
  forestMid: '#4E6B5E',
  forestLight: '#52B788',
  forestDark: '#40966F',
  forestPale: '#E8F5EE',
  slate: '#7D9084',
  borderSubtle: '#B5C2BA',
  goldBg: '#FFF3B0',
  goldBorder: '#F4C542',
  cardAlt: '#FDFEFE',

  // Colores canónicos de tarjetas A4 para niños
  colorDefs: [
    { name: 'ROJO',     hex: '#E52521', fg: '#FFFFFF' },
    { name: 'AZUL',     hex: '#1A73E8', fg: '#FFFFFF' },
    { name: 'AMARILLO', hex: '#F9C623', fg: '#000000' },
    { name: 'VERDE',    hex: '#5BA848', fg: '#FFFFFF' },
    { name: 'VIOLETA',  hex: '#6B4EA2', fg: '#FFFFFF' },
    { name: 'NARANJA',  hex: '#F5821F', fg: '#FFFFFF' },
    { name: 'MARRON',   hex: '#7A3E1D', fg: '#FFFFFF' },
    { name: 'BLANCO',   hex: '#FFFFFF', fg: '#000000' },
    { name: 'NEGRO',    hex: '#000000', fg: '#FFFFFF' },
  ],

  // Colores por letra A-Z en Libro A4
  letterColors: {
    A: '#F1C232',
    B: '#5DA84D',
    C: '#3D98D3',
    D: '#E06666',
    E: '#93C47D',
    F: '#F6B26B',
    G: '#8E7CC3',
    H: '#FFD966',
    I: '#76A5AF',
    J: '#CC4125',
    K: '#674EA7',
    L: '#45818E',
    M: '#38761D',
    N: '#B45F06',
    Ñ: '#A64D79',
    O: '#C27BA0',
    P: '#1155CC',
    Q: '#783F04',
    R: '#A61C1C',
    S: '#3C78D8',
    T: '#274E13',
    U: '#6AA84F',
    V: '#4A86E8',
    W: '#0B5394',
    X: '#741B47',
    Y: '#990000',
    Z: '#5B3968',
  } as Record<string, string>,

  // Pasteles de las tarjetas de números 1-10 en Libro A4
  numberPastels: [
    '#FFB4A2', '#FFD6A5', '#FFF3B0', '#D8F3A8', '#B8E0D2',
    '#BDE0FE', '#C7CEEA', '#D6B8F5', '#FFAFCC', '#FFC8DD',
  ],
} as const;
